import { Divider, InputGroup, Intent, Popover, Position } from '@blueprintjs/core';
import classNames from 'classnames';
import { Button } from 'components/buttons';
import { FormGroupedCheckboxItems } from 'components/form-fields';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import 'components/form-fields/FormFields.css';
import 'components/buttons/Buttons.css';

export function FormListSelector(props) {

    const [allItemList, setAllItemList] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [popoverOpen, setPopoverOpen] = useState(false);
    const [dropDownName, setDropDownName] = useState("Select");

    const groupIdentifier = "group";

    useEffect(() => {
        const list = [{
            id: 0,
            name: `All ${props.itemsName}`,
            type: "group",
            items: props.itemsList,
            expanded: true
        }];

        if (!props.loading && props.selectedItems) {
            checkItems(list);
        }

        setAllItemList(list);
    }, [props.itemsList, props.itemsName, props.selectedItems, props.loading]);

    function onPopoverInteraction(shouldOpen) {
        setPopoverOpen(shouldOpen);
    }

    function onSearchChange(event) {
        setSearchTerm(event.target.value);
        applySearchTerm(allItemList, event.target.value);
    }

    function findRecursiveItem(structure, id, type) {
        var response = null;

        if (structure != null) {
            for (var i = 0; i < structure.length; i++) {
                var currentItem = structure[i];

                if ((currentItem.type === type || currentItem.type == null) && currentItem.id === id) {
                    return currentItem;
                }

                if (currentItem.type?.toLowerCase() === groupIdentifier) {
                    var innerResponse = findRecursiveItem(currentItem.items, id, type);
                    if (innerResponse != null) {
                        response = innerResponse;
                    }
                }

            }
        }

        return response;
    }

    function setChecked(item, innerItemsCheckedState) {
        item.checked = innerItemsCheckedState.all;
        item.partialChecked = !innerItemsCheckedState.all && innerItemsCheckedState.some;
    }

    useEffect(() => {
        if (props.selectedItems.length === 0) {
            setDropDownName(`Select ${props.itemsName}`);
        } else if (props.selectedItems.length === 1) {
            var singleItem = findRecursiveItem(allItemList, props.selectedItems[0], "item");
            setDropDownName(singleItem?.name);
        } else {
            setDropDownName(`${props.itemsName} (${props.selectedItems.length} selected)`);
        }

    }, [props.itemsList, props.selectedItems, props.itemsName, allItemList]);

    function checkItems(items) {
        var checkedState = {
            all: true,
            some: false
        };

        if (items?.length > 0) {
            items.map(function (item) {
                if (item.type?.toLowerCase() === groupIdentifier) {
                    setChecked(item, checkItems(item.items));
                } else {
                    item.checked = props.selectedItems?.some(i => i === item.id);
                }

                if (!item.checked) {
                    checkedState.all = false;
                } else {
                    checkedState.some = true;
                }

                return item;
            });
        }

        return checkedState;
    }

    function onGroupSelect(id) {

        var clonedSelectedItems = [
            ...props.selectedItems
        ];

        var groupItems = findRecursiveItem(allItemList, id, groupIdentifier);
        if (groupItems?.items) {
            setSelectedItems(groupItems.items, clonedSelectedItems, !groupItems.checked);
            props.setSelectedItems(clonedSelectedItems);
        }
    }

    function onGroupToggle(id) {

        var clonedItems = [
            ...allItemList
        ];

        var group = findRecursiveItem(clonedItems, id, "group");
        group.expanded = !group.expanded;

        setAllItemList(clonedItems);
    }

    function setSelectedItems(items, selectedItems, checkedState) {
        items.map(function (item) {
            if (item?.type?.toLowerCase() === groupIdentifier) {
                setSelectedItems(item.items, selectedItems, checkedState)
            } else {
                if (checkedState && !selectedItems?.some(i => i === item.id)) {
                    selectedItems.push(item.id);
                }

                if (!checkedState) {
                    var index = selectedItems.indexOf(item.id);

                    if (index > -1) {
                        selectedItems.splice(index, 1);
                    }
                }
            }
        });
    }

    function onItemSelect(id) {

        var clonedSelectedItemList = [
            ...props.selectedItems
        ];

        var index = clonedSelectedItemList.indexOf(id);

        if (index > -1) {
            clonedSelectedItemList.splice(index, 1);
        } else {
            clonedSelectedItemList.push(id);
        }

        //This should kick off the useEffect where we'll do the update
        props.setSelectedItems(clonedSelectedItemList);

        if (props.singleSelection) {
            setPopoverOpen(false);
        }
    }

    function applySearchTerm(itemList, searchTermToApply) {

        var searchReturnedResults = false;
        var anyChildrenReturnedResults = false;

        if (itemList == null) {
            return false;
        }

        for (var i = 0; i < itemList.length; i++) {
            var currentItem = itemList[i];
            currentItem.appearInSearch = false;

            if ((currentItem.type === "item" || currentItem.type == null) && (currentItem.name.toLowerCase().indexOf(searchTermToApply.toLowerCase()) > -1)) {
                currentItem.appearInSearch = true;
                searchReturnedResults = true;
            }

            var childSearchReturnedResults = applySearchTerm(currentItem.items, searchTermToApply);

            if (childSearchReturnedResults) {
                currentItem.appearInSearch = true;
            }

            anyChildrenReturnedResults = anyChildrenReturnedResults || childSearchReturnedResults;
        }

        return searchReturnedResults || anyChildrenReturnedResults;
    }

    return (
        <div className="form-field">
            <Popover
                onInteraction={onPopoverInteraction}
                isOpen={popoverOpen}
                content={
                    <div className={classNames("grouped-checkbox-list-container", { "bp6-skeleton": props.loading })}>


                        <div className="location-selector-search-container">
                            <InputGroup
                                value={searchTerm}
                                onChange={onSearchChange}
                                size="large"
                                placeholder={props.searchPlaceholder}
                                maxLength={50}
                                disabled={props.loading}
                            />
                        </div>

                        <Divider />

                        <div className="dropdown-list-scroll">
                            <FormGroupedCheckboxItems
                                items={allItemList}
                                searchTerm={searchTerm}
                                groupsSelectable={!props.singleSelection}
                                onGroupToggle={onGroupToggle}
                                onItemSelect={onItemSelect}
                                onGroupSelect={onGroupSelect}
                                loading={props.loading}
                                filter={null}
                                disabled={props.disabled}
                                singleSelection={props.singleSelection}

                            />
                        </div>
                    </div>
                } position={Position.BOTTOM_LEFT}
                minimal={true}
                canEscapeKeyClose={true}
                onOpened={props.onListOpened}
                onClose={props.onListClose}
            >
                <Button className="bp6-button bp6-large btn-form btn-border grouped-checkbox-list-button" disabled={props.disabled} icon={props.itemsIcon} loading={props.loading} text={dropDownName} intent={Intent.NONE} minimal={true} large={false} onClick={() => { }} />
            </Popover>
        </div>
    );
}

FormListSelector.propTypes = {
    itemsList: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.any.isRequired,
            name: PropTypes.string.isRequired
        })
    ).isRequired,
    selectedItems: PropTypes.array.isRequired,
    setSelectedItems: PropTypes.func.isRequired,
    searchPlaceholder: PropTypes.string.isRequired,
    itemsName: PropTypes.string.isRequired,
    itemsIcon: PropTypes.string,
    loading: PropTypes.bool,
    onListOpened: PropTypes.func,
    onListClose: PropTypes.func
};